import { memo, useCallback, useMemo } from 'react';
import { itemsInventoryFormatPrice } from '../../helpers/itemsInventory';
import { ItemsInventoryListItemSwitcher } from '../ItemsInventoryListItemSwitcher/ItemsInventoryListItemSwitcher';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { IDeal, IMeal } from 'src/types/combo';
import { TBooleanInNumber } from 'src/types/main';
import { IProductV4, PRODUCT_TYPE } from 'src/types/product';
import { booleanNumberReverse, getOrderTypeIconsNames, getTranslate, hasRole } from 'src/utils/helpers';
import Image from 'src/view/components/Image/Image';
import OrderTypes from 'src/view/components/OrderTypes/OrderTypes';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { productUtils } from '~/utils/productUtils';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { ProductWarning, ProductWarningType, TProductWarningType } from '~/view/components/ProductWarning';
import './style.scss';
import { USER_ROLES } from '~/constants/user';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import { RESTRICTION_ACCESS } from '~/constants/integrations';
import { localeStore } from '~/mobx/localesStore';

interface IProps {
  item: IProductV4 | IDeal | IMeal;
  onChangeStock: (id: number, stock: TBooleanInNumber) => void;
  onChangeStatus: (id: number, status: TBooleanInNumber) => void;
  branchFilter?: IPickerOption;
}

export const ItemsInventoryListItem: React.FC<IProps> = memo((props: IProps): JSX.Element => {
  const { item, branchFilter } = props;

  const isCombo = useMemo((): boolean => Boolean(item.type), [item.type]);
  const orderTypeIconNames = useMemo((): Icons[] => getOrderTypeIconsNames(item.order_type), [item.order_type]);

  const onChangeStock = useCallback((stock: TBooleanInNumber) => props.onChangeStock(item.id, booleanNumberReverse(stock)), [item]);
  const onChangeStatus = useCallback((status: TBooleanInNumber) => props.onChangeStatus(item.id, status), [item]);

  const warningType = useMemo((): TProductWarningType => {
    return item.type === PRODUCT_TYPE.DEAL_COMBO ? ProductWarningType.DEAL_ITEM_BRANCH : ProductWarningType.MEAL_ITEM_BRANCH;
  }, [item.type]);

  const switcherAccess = useMemo(() => {
    return hasRole([USER_ROLES.FRANCHISEE, USER_ROLES.CASHIER, USER_ROLES.OPERATOR]) ? RESTRICTION_ACCESS.DISABLED : RESTRICTION_ACCESS.ALLOWED;
  }, []);

  return (
    <li className="items-inventory-list-item items-inventory-list-item__grid-template">
      <div className="items-inventory-list-item__element">
        <Image
          src={item.image || ''}
          alt={getTranslate(item.name) + '-image'}
          wrapperClassName="items-inventory-list-item__image mr-4"
          className="full-height"
          aspectRatio="none"
        />

        <div className="items-inventory-list-item__name-container">
          <div className="d-flex flex-row align-items-center">
            <Text type={TEXT.LIST_ROW} text={item.name} className="items-inventory-list-item__name" />
            {isCombo ? <ProductWarning type={warningType} combo={item} /> : null}
          </div>

          {item.is_donation ? <Text type={TEXT.DONATABLE_IN_LIST} text="donatable" /> : null}
        </div>
      </div>

      <div className="items-inventory-list-item__element">
        <Text type={TEXT.LIST_ROW} text={item.category?.name} />
        {isCombo ? <ProductWarning type="categoryInList" combo={item} /> : null}
      </div>

      <div className="items-inventory-list-item__element">
        <OrderTypes types={orderTypeIconNames} />
      </div>

      <div className="items-inventory-list-item__element">
        <Text type={TEXT.LIST_ROW} text={itemsInventoryFormatPrice(productUtils.getProductPriceInList(item), branchFilter)} noTranslate />
      </div>

      <div className="items-inventory-list-item__element">
        <Text type={TEXT.LIST_ROW} text={item.sort} />
      </div>

      <ItemsInventoryListItemSwitcher
        type="stock"
        status={booleanNumberReverse(item.out_of_stock)}
        onChange={onChangeStock}
        isCombo={isCombo}
        branchFilterId={branchFilter?.value}
      />

      <IntegrationManager
        setting={switcherAccess}
        wrapperClassName="items-list-row__border d-flex flex-row align-items-center w-5"
        tooltipOptions={{ minWidth: 355, message: localeStore.t('no_permission') }}
        withTooltip
      >
        <ItemsInventoryListItemSwitcher
          type="status"
          status={item.status}
          onChange={onChangeStatus}
          isCombo={isCombo}
          branchFilterId={branchFilter?.value}
        />
      </IntegrationManager>
    </li>
  );
});
